import React from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import StatusPill from 'src/components-new/primitives/StatusPill'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'

export type VMRecord = {
  _id: string
  startDate: string
  endDate: string
  title: string
  participants: Record<string, any>[]
  participantsReadable?: string
  appointmentStatus?: string
  linkTeleconsultation?: string
  creatorId?: string
  [key: string]: any
}

export const STATUS_OPTIONS: {value: string; label: string}[] = [
  {value: 'PLANNING', label: 'Da pianificare'},
  {value: 'PLANNED', label: 'Pianificato'},
  {value: 'COMPLETED', label: 'Completato'},
  {value: 'CANCELED', label: 'Cancellato'},
]

function getStatusLabel(status: string | undefined): string {
  return STATUS_OPTIONS.find(opt => opt.value === status)?.label ?? '—'
}

function getStatusColor(status: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'PLANNED':
    case 'COMPLETED':
      return 'success'
    case 'CANCELED':
      return 'error'
    case 'PLANNING':
      return 'warning'
    default:
      return 'default'
  }
}

interface BuildColumnsOptions {
  onDelete: (row: VMRecord) => void
  authUserId?: string
  columnOptions?: {id: string; label: string; format?: string}[]
}

/**
 * Some PSP configs list the "ORARIO" column with a mismatched `id` that doesn't
 * exist on the record (instead of the expected `id: 'startDate'` + a time
 * format, mirroring how "DATA" is configured). Detect it via label/format so
 * it still derives its value from `startDate` instead of always rendering '—'.
 */
function looksLikeTimeColumn(col: {label: string; format?: string}): boolean {
  const label = col.label.trim().toLowerCase()
  if (label === 'orario' || label === 'ora') return true
  return (col.format ?? '').toLowerCase().includes('hh')
}

export interface ParticipantRow {
  key: string
  firstName?: string
  lastName?: string
  email?: string
  qualification?: string
  [key: string]: any
}

const DEFAULT_PARTICIPANT_COLUMN_OPTIONS: {id: string; label: string}[] = [
  {id: 'firstName', label: 'Nome'},
  {id: 'lastName', label: 'Cognome'},
  {id: 'email', label: 'Email'},
  {id: 'qualification', label: 'Qualifica'},
]

/**
 * Builds the "Partecipanti" sub-table columns for the virtual meeting detail
 * dialogs from the PSP's `detailColumnOptions` config (mirrors `buildColumns`
 * above, which does the same for the main list). Falls back to the default
 * Nome/Cognome/Email/Qualifica set when the PSP hasn't configured any.
 */
export function buildParticipantColumns(
  detailColumnOptions?: {id: string; label: string}[]
): ColumnDef<ParticipantRow>[] {
  const options = detailColumnOptions?.length ? detailColumnOptions : DEFAULT_PARTICIPANT_COLUMN_OPTIONS
  return options.map((col) => ({
    key: col.id,
    header: col.label,
    render: (row: ParticipantRow) => (
      <Typography variant="body2">{row[col.id] ?? '—'}</Typography>
    ),
  }))
}

export function buildColumns({onDelete, authUserId, columnOptions}: BuildColumnsOptions): ColumnDef<VMRecord>[] {
  const actionsCol: ColumnDef<VMRecord> = {
    key: 'actions',
    header: '',
    width: 56,
    isActionColumn: true,
    render: (row) =>
      row.creatorId === authUserId ? (
        <Box onClick={(e) => e.stopPropagation()}>
          <ActionIconButton variant="delete" tooltip="Elimina" onClick={() => onDelete(row)} />
        </Box>
      ) : null,
  }

  if (columnOptions && columnOptions.length > 0) {
    let startDateSortAssigned = false
    const extraCols: ColumnDef<VMRecord>[] = columnOptions.map((col, idx) => {
      const isTimeColumn = looksLikeTimeColumn(col)
      if (col.id === 'startDate' || isTimeColumn) {
        // Only the date-formatted entry gets the sort control, not the time one.
        const assignSort = !isTimeColumn && !startDateSortAssigned
        if (assignSort) startDateSortAssigned = true
        const defaultFormat = isTimeColumn ? 'HH:mm' : 'ddd DD MMM YYYY'
        return {
          key: `${col.id}-${idx}`,
          header: col.label,
          sortKey: assignSort ? 'startDate' : undefined,
          render: (row: VMRecord) => (
            <Typography variant="body2">
              {row.startDate ? moment(row.startDate).format(col.format || defaultFormat) : '—'}
            </Typography>
          ),
        }
      }
      if (col.id === 'participantsReadable') {
        return {
          key: `${col.id}-${idx}`,
          header: col.label,
          render: (row: VMRecord) => (
            <Typography variant="body2">{row.participantsReadable || '—'}</Typography>
          ),
        }
      }
      if (col.id === 'appointmentStatus') {
        return {
          key: `${col.id}-${idx}`,
          header: col.label,
          render: (row: VMRecord) => (
            <StatusPill label={getStatusLabel(row.appointmentStatus)} color={getStatusColor(row.appointmentStatus)} />
          ),
        }
      }
      return {
        key: `${col.id}-${idx}`,
        header: col.label,
        render: (row: VMRecord) => (
          <Typography variant="body2">{(row as any)[col.id] ?? '—'}</Typography>
        ),
      }
    })
    return [...extraCols, actionsCol]
  }

  const defaultCols: ColumnDef<VMRecord>[] = [
    {
      key: 'date',
      header: 'DATA',
      sortKey: 'startDate',
      render: (row) => (
        <Typography variant="body2">
          {row.startDate ? moment(row.startDate).format('ddd DD MMM YYYY') : '—'}
        </Typography>
      ),
    },
    {
      key: 'time',
      header: 'ORARIO',
      render: (row) => (
        <Typography variant="body2">
          {row.startDate ? moment(row.startDate).format('HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      key: 'title',
      header: 'TITOLO',
      hideInMobileCard: true,
      render: (row) => <Typography variant="body2">{row.title ?? '—'}</Typography>,
    },
    {
      key: 'participantsReadable',
      header: 'PARTECIPANTI',
      render: (row) => <Typography variant="body2">{row.participantsReadable || '—'}</Typography>,
    },
    {
      key: 'appointmentStatus',
      header: 'STATO',
      render: (row) => (
        <StatusPill label={getStatusLabel(row.appointmentStatus)} color={getStatusColor(row.appointmentStatus)} />
      ),
    },
    actionsCol,
  ]

  return defaultCols
}
